import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { format, formatDistanceToNowStrict, isToday, isYesterday, parseISO } from 'date-fns';

/** Tailwind-aware className joiner. */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/* -------------------------------------------------------------------------- */
/* Numbers                                                                    */
/* -------------------------------------------------------------------------- */

export const formatNumber = (value: number | undefined | null) =>
  value === undefined || value === null ? '—' : value.toLocaleString('en-US');

export const formatPercent = (value: number | undefined | null, digits = 1) =>
  value === undefined || value === null ? '—' : `${value.toFixed(digits)}%`;

/** 18560 -> "18.6k"; used in tight chart axes. */
export function formatCompact(value: number) {
  if (Math.abs(value) < 1000) return String(value);
  return new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 }).format(
    value,
  );
}

/* -------------------------------------------------------------------------- */
/* Dates                                                                      */
/* -------------------------------------------------------------------------- */

const toDate = (value: string | Date) => (typeof value === 'string' ? parseISO(value) : value);

export const formatDate = (value?: string | Date | null, pattern = 'dd MMM yyyy') =>
  value ? format(toDate(value), pattern) : '—';

export const formatDateTime = (value?: string | Date | null) =>
  value ? format(toDate(value), 'dd MMM yyyy, hh:mm a') : '—';

export const formatTime = (value?: string | Date | null) =>
  value ? format(toDate(value), 'hh:mm a') : '—';

/** "Today, 10:35 AM" / "Yesterday, 04:05 PM" / "14 Jul 2025, 09:12 AM" */
export function formatSmartDateTime(value?: string | Date | null) {
  if (!value) return '—';
  const date = toDate(value);
  if (isToday(date)) return `Today, ${format(date, 'hh:mm a')}`;
  if (isYesterday(date)) return `Yesterday, ${format(date, 'hh:mm a')}`;
  return format(date, 'dd MMM yyyy, hh:mm a');
}

export const formatRelative = (value?: string | Date | null) =>
  value ? `${formatDistanceToNowStrict(toDate(value))} ago` : '—';

/** ISO `yyyy-MM-dd` for date inputs and range query params. */
export const toIsoDate = (value: Date) => format(value, 'yyyy-MM-dd');

/* -------------------------------------------------------------------------- */
/* Misc                                                                       */
/* -------------------------------------------------------------------------- */

export function initials(name: string) {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]!.toUpperCase())
    .join('');
}

/** Triggers a browser download for a Blob received from the API. */
export function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

/** Builds the "Showing 1 to 10 of 50 entries" label used under every table. */
export function pageRangeLabel(page: number, size: number, total: number) {
  if (total === 0) return 'No entries to show';
  const from = page * size + 1;
  const to = Math.min(total, page * size + size);
  return `Showing ${from} to ${to} of ${total.toLocaleString()} entries`;
}

/** Compact page-number list with ellipses, e.g. [0,1,2,'…',9]. */
export function paginationRange(current: number, totalPages: number, siblings = 1) {
  const range: Array<number | '…'> = [];
  const last = totalPages - 1;
  if (totalPages <= 1) return [0];

  const start = Math.max(1, current - siblings);
  const end = Math.min(last - 1, current + siblings);

  range.push(0);
  if (start > 1) range.push('…');
  for (let page = start; page <= end; page += 1) range.push(page);
  if (end < last - 1) range.push('…');
  if (last > 0) range.push(last);

  return range;
}
